import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';

import CustomerMainPageLayout from '@/layout/CustomerMainLayout/CustomerMainLayout';
import AuthLayout from '@/layout/AuthLayout/AuthLayout'
import SixListing from '@/pages/SixListing/SixListing';
import Register from '@/pages/Register/Register';
import ResetPassword from '@/pages/ResetPassword/ResetPassword';
import ForgotPassword from '@/pages/ForgotPassword/ForgotPassword';
import SixDetailPage from '@/pages/SixDetail/SixDetailPage';
import LoginCredentials from '@/pages/Login/LoginCredentials';
import LoginSelect2FA from '@/pages/Login/LoginSelect2FA';
import LoginVerify2FA from '@/pages/Login/LoginVerify2fa';
import PubsubTest from '@/pages/PubsubTest/PubsubTest';
import RecoveryVerifyEmailOtpPage from '@/pages/PasswordRecovery/RecoveryVerifyEmailOtp';
import RecoveryVerifyPhoneOtpPage from '@/pages/PasswordRecovery/RecoveryVerifyPhoneOtp';
import RecoverySetPasswordPage from '@/pages/PasswordRecovery/RecoverySetPassword';
import { getMeThunk } from '@/store/thunks/user/getMeThunk';
// import { getCoturnCredentialsThunk } from '@/store/thunks/organization/getCoturnCredentialsThunk';
import { store } from '@/store'; // Adjust the import path to your store
import { getRefreshToken, getSessionToken, getUkekFromStorage } from '@/utils/sessionStorage';
// import { getOrganizationIdFromToken, getUserIdFromToken, isJWTExpired } from '@/store/utils/jwtDecoder';
import { getUserIdFromToken, isJWTExpired } from '@/store/utils/jwtDecoder';
import { setUkek, setUserId } from '@/store/slices/auth/authSlice';
import { fetchSixCatalog } from '@/store/thunks/sixCatalog/fetchSixCatalog';

// --- Route Definitions ---
const rootRoute = createRootRoute({
  component: Outlet,
});

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth',
  component: AuthLayout,
});

const registrationRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/registration',
  component: Register,
});

// Login routes
const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/login',
  component: LoginCredentials,
});

const login2FASelectRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/login/2fa/select',
  component: LoginSelect2FA,
  });

const login2FAVerifyRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/login/2fa/verify',
  component: LoginVerify2FA,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/forgot-password',
  component: ResetPassword
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/reset-password',
  component: ForgotPassword
});

const recoverPasswordOtpRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/reset-password/otp-verify',
  component: RecoveryVerifyEmailOtpPage
});

const recoverPasswordTwoFactorRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/reset-password/two-factor-verify',
  component: RecoveryVerifyPhoneOtpPage
});

const recoverSetPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/reset-password/set-password',
  component: RecoverySetPasswordPage
});

// PubSub test route (public, no auth required)
const pubsubTestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pubsub-test',
  component: PubsubTest
});

// App layout route with beforeLoad to fetch user
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  component: CustomerMainPageLayout,
  beforeLoad: async({ location }) => {
    // Check if token exists
    const token = getSessionToken();
    if (token == null) {
      return redirect({
        to: '/login',
        search: { redirect: location.pathname },
      });
    }
    const userId = getUserIdFromToken(token);
    const ukek = store.getState().auth.ukek;
    if (ukek === '') {
      if(userId !== '') {
        const storedUkek = getUkekFromStorage(userId);
        if(storedUkek === null) {
      return redirect({
        to: '/login',
        search: { redirect: location.pathname },
      });
    }
    else {
      store.dispatch(setUkek(storedUkek));
      store.dispatch(setUserId(userId));
    }
    }
  }
    // Token exists and is valid, fetch user data
    try {
      await store.dispatch(getMeThunk(userId)).unwrap();

      // Fetch coturn credentials for WebRTC TURN server authentication
      // const organizationId = getOrganizationIdFromToken(token)
      // await store.dispatch(getCoturnCredentialsThunk(organizationId)).unwrap();
    } catch (error) {
      redirect({
        to: '/login',
        search: { redirect: location.href }
      });
      throw error;
    }
  },
});

const sixListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'sixes',
  component: SixListing,
});

const sixDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'sixes/$sixId',
  component: SixDetailPage,
  validateSearch: (search: Record<string, unknown>): { path?: string } => ({
      path: typeof search.path === 'string' ? search.path : undefined,
    }),
  beforeLoad: async({ params }) => {
    const state = store.getState();
    const connectionState = state.p2pConnection.connections[params.sixId];

    // If P2P not connected for this six, trigger catalog fetch which initializes P2P
    if (connectionState?.status !== 'connected') {
      await store.dispatch(fetchSixCatalog(undefined)).unwrap();
    }
  },
});

// Redirect routes
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  loader: () => redirect({ to: '/login' }),
});

const appIndexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  loader: () => redirect({ to: '/app/sixes' }),
});

const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren([
    registrationRoute,
    loginRoute,
    login2FASelectRoute,
    login2FAVerifyRoute,
    resetPasswordRoute,
    forgotPasswordRoute,
    recoverPasswordOtpRoute,
    recoverPasswordTwoFactorRoute,
    recoverSetPasswordRoute
  ]),
  indexRoute,
  pubsubTestRoute,
  appLayoutRoute.addChildren([appIndexRoute, sixListRoute, sixDetailRoute]),
]);

export const router = createRouter({
  routeTree,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
